import type { SiteConnection, Space, SpaceFootprint } from "../types";

export type PassageState = "open" | "frontier" | "survey";
export type PassageAxis = "horizontal" | "vertical";
export type RoomEdge = "west" | "east" | "up" | "down";

export interface BunkerPoint {
  x: number;
  y: number;
}

export interface SpaceRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  footprint: SpaceFootprint;
}

export interface PassageSegment {
  id: string;
  connectionId: string;
  axis: PassageAxis;
  left: number;
  top: number;
  length: number;
  state: PassageState;
  order: number;
  sharedWall: boolean;
}

export interface BunkerRoute {
  id: string;
  from: string;
  to: string;
  state: PassageState;
  kind: PassageAxis | "dogleg" | "door";
  fromPort: RoomEdge;
  toPort: RoomEdge;
  fromPoint: BunkerPoint;
  toPoint: BunkerPoint;
  bend?: BunkerPoint;
  bends?: BunkerPoint[];
  sharedWall: boolean;
  segments: PassageSegment[];
}

export interface ConnectionPort {
  id: string;
  side: RoomEdge;
  /** Percentage along the relevant room edge. */
  offset: number;
  sharedWall: boolean;
}

/**
 * Percentage-space structural grid. Space x/y values are top-left bay anchors;
 * the seam is the narrow construction joint left between occupied bays.
 */
export const BUNKER_GRID = {
  originX: 1.5,
  originY: 19.5,
  unitWidth: 19.2,
  unitHeight: 21.7,
  seamX: 0.8,
  seamY: 0.9,
} as const;

export const BUNKER_WORLD = {
  width: 1080,
  height: 540,
} as const;

const LEGACY_FOOTPRINTS: Record<Space["size"], SpaceFootprint> = {
  small: { width: 1, height: 1 },
  medium: { width: 2, height: 1 },
  large: { width: 2, height: 2 },
};

const EPSILON = 0.001;

function positiveGridUnits(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.max(1, Math.round(value))
    : fallback;
}

/** Schema-three payloads did not carry footprints, so size remains a safe fallback. */
export function spaceFootprint(space: Space): SpaceFootprint {
  const legacy = LEGACY_FOOTPRINTS[space.size] ?? LEGACY_FOOTPRINTS.small;
  const footprint = space.footprint ?? space.room?.footprint;
  return {
    width: positiveGridUnits(footprint?.width, legacy.width),
    height: positiveGridUnits(footprint?.height, legacy.height),
  };
}

/** Clamp a top-left nested room footprint to the chamber it occupies. */
export function builtFootprintForSpace(
  space: Space,
  requested: SpaceFootprint | undefined = space.room?.footprint,
): SpaceFootprint {
  const capacity = spaceFootprint(space);
  const footprint = requested ?? capacity;
  return {
    width: Math.max(1, Math.min(capacity.width, Math.round(footprint.width))),
    height: Math.max(1, Math.min(capacity.height, Math.round(footprint.height))),
  };
}

export function spaceRect(space: Space): SpaceRect {
  const footprint = spaceFootprint(space);
  const x = Number.isFinite(space.x) ? space.x : 0;
  const y = Number.isFinite(space.y) ? space.y : 0;
  const left = BUNKER_GRID.originX + x * BUNKER_GRID.unitWidth;
  const top = BUNKER_GRID.originY + y * BUNKER_GRID.unitHeight;
  const width = footprint.width * BUNKER_GRID.unitWidth - BUNKER_GRID.seamX;
  const height = footprint.height * BUNKER_GRID.unitHeight - BUNKER_GRID.seamY;
  return {
    left,
    right: left + width,
    top,
    bottom: top + height,
    width,
    height,
    centerX: left + width / 2,
    centerY: top + height / 2,
    footprint,
  };
}

/** Exact share of a chamber occupied by a nested room, including grid seams. */
export function nestedFootprintPercent(
  room: SpaceFootprint,
  chamber: SpaceFootprint,
): { width: number; height: number } {
  const roomWidth = room.width * BUNKER_GRID.unitWidth - BUNKER_GRID.seamX;
  const roomHeight = room.height * BUNKER_GRID.unitHeight - BUNKER_GRID.seamY;
  const chamberWidth = chamber.width * BUNKER_GRID.unitWidth - BUNKER_GRID.seamX;
  const chamberHeight = chamber.height * BUNKER_GRID.unitHeight - BUNKER_GRID.seamY;
  return {
    width: roomWidth / chamberWidth * 100,
    height: roomHeight / chamberHeight * 100,
  };
}

function gridBounds(space: Space) {
  const footprint = spaceFootprint(space);
  return {
    left: space.x,
    right: space.x + footprint.width,
    top: space.y,
    bottom: space.y + footprint.height,
  };
}

/** True only when structural bays overlap; touching edges are valid neighbours. */
export function footprintsOverlap(left: Space, right: Space): boolean {
  const a = gridBounds(left);
  const b = gridBounds(right);
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function passageState(left: Space, right: Space): PassageState {
  if (left.state === "excavated" && right.state === "excavated") return "open";
  if (left.state === "excavated" || right.state === "excavated") return "frontier";
  return "survey";
}

function horizontalSegment(
  connectionId: string,
  order: number,
  fromX: number,
  toX: number,
  y: number,
  state: PassageState,
  sharedWall = false,
): PassageSegment {
  return {
    id: `${connectionId}-segment-${order}`,
    connectionId,
    axis: "horizontal",
    left: Math.min(fromX, toX),
    top: y,
    length: Math.abs(toX - fromX),
    state,
    order,
    sharedWall,
  };
}

function verticalSegment(
  connectionId: string,
  order: number,
  x: number,
  fromY: number,
  toY: number,
  state: PassageState,
  sharedWall = false,
): PassageSegment {
  return {
    id: `${connectionId}-segment-${order}`,
    connectionId,
    axis: "vertical",
    left: x,
    top: Math.min(fromY, toY),
    length: Math.abs(toY - fromY),
    state,
    order,
    sharedWall,
  };
}

function overlapMidpoint(aStart: number, aEnd: number, bStart: number, bEnd: number): number | null {
  const start = Math.max(aStart, bStart);
  const end = Math.min(aEnd, bEnd);
  return end - start > EPSILON ? start + (end - start) / 2 : null;
}

function sharedWallRoute(
  connection: SiteConnection,
  fromSpace: Space,
  toSpace: Space,
  state: PassageState,
): BunkerRoute | null {
  const fromGrid = gridBounds(fromSpace);
  const toGrid = gridBounds(toSpace);
  const from = spaceRect(fromSpace);
  const to = spaceRect(toSpace);

  const sharedY = overlapMidpoint(from.top, from.bottom, to.top, to.bottom);
  if (sharedY !== null && Math.abs(fromGrid.right - toGrid.left) < EPSILON) {
    const fromPoint = { x: from.right, y: sharedY };
    const toPoint = { x: to.left, y: sharedY };
    return {
      ...connection, state, kind: "door", fromPort: "east", toPort: "west",
      fromPoint, toPoint, sharedWall: true,
      segments: [horizontalSegment(connection.id, 0, fromPoint.x, toPoint.x, sharedY, state, true)],
    };
  }
  if (sharedY !== null && Math.abs(toGrid.right - fromGrid.left) < EPSILON) {
    const fromPoint = { x: from.left, y: sharedY };
    const toPoint = { x: to.right, y: sharedY };
    return {
      ...connection, state, kind: "door", fromPort: "west", toPort: "east",
      fromPoint, toPoint, sharedWall: true,
      segments: [horizontalSegment(connection.id, 0, fromPoint.x, toPoint.x, sharedY, state, true)],
    };
  }

  const sharedX = overlapMidpoint(from.left, from.right, to.left, to.right);
  if (sharedX !== null && Math.abs(fromGrid.bottom - toGrid.top) < EPSILON) {
    const fromPoint = { x: sharedX, y: from.bottom };
    const toPoint = { x: sharedX, y: to.top };
    return {
      ...connection, state, kind: "door", fromPort: "down", toPort: "up",
      fromPoint, toPoint, sharedWall: true,
      segments: [verticalSegment(connection.id, 0, sharedX, fromPoint.y, toPoint.y, state, true)],
    };
  }
  if (sharedX !== null && Math.abs(toGrid.bottom - fromGrid.top) < EPSILON) {
    const fromPoint = { x: sharedX, y: from.top };
    const toPoint = { x: sharedX, y: to.bottom };
    return {
      ...connection, state, kind: "door", fromPort: "up", toPort: "down",
      fromPoint, toPoint, sharedWall: true,
      segments: [verticalSegment(connection.id, 0, sharedX, fromPoint.y, toPoint.y, state, true)],
    };
  }
  return null;
}

interface RouteObstacle {
  id: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface RoutePortal {
  side: RoomEdge;
  point: BunkerPoint;
  stub: BunkerPoint;
}

const ROUTE_CLEARANCE_X = 2.3;
const ROUTE_CLEARANCE_Y = 2.6;
const PORT_ORDER: RoomEdge[] = ["east", "down", "west", "up"];

function routeObstacles(spaces: Space[]): RouteObstacle[] {
  return spaces.map((space) => {
    const rect = spaceRect(space);
    return {
      id: space.id,
      left: rect.left - ROUTE_CLEARANCE_X,
      right: rect.right + ROUTE_CLEARANCE_X,
      top: rect.top - ROUTE_CLEARANCE_Y,
      bottom: rect.bottom + ROUTE_CLEARANCE_Y,
    };
  });
}

function routePortal(rect: SpaceRect, obstacle: RouteObstacle, side: RoomEdge): RoutePortal {
  if (side === "west") return { side, point: { x: rect.left, y: rect.centerY }, stub: { x: obstacle.left, y: rect.centerY } };
  if (side === "east") return { side, point: { x: rect.right, y: rect.centerY }, stub: { x: obstacle.right, y: rect.centerY } };
  if (side === "up") return { side, point: { x: rect.centerX, y: rect.top }, stub: { x: rect.centerX, y: obstacle.top } };
  return { side, point: { x: rect.centerX, y: rect.bottom }, stub: { x: rect.centerX, y: obstacle.bottom } };
}

function pointInsideObstacle(point: BunkerPoint, obstacle: RouteObstacle): boolean {
  return point.x > obstacle.left + EPSILON && point.x < obstacle.right - EPSILON
    && point.y > obstacle.top + EPSILON && point.y < obstacle.bottom - EPSILON;
}

function segmentAvoidsObstacles(
  from: BunkerPoint,
  to: BunkerPoint,
  obstacles: RouteObstacle[],
): boolean {
  if (Math.abs(from.y - to.y) < EPSILON) {
    const left = Math.min(from.x, to.x);
    const right = Math.max(from.x, to.x);
    return obstacles.every((obstacle) =>
      from.y <= obstacle.top + EPSILON
      || from.y >= obstacle.bottom - EPSILON
      || right <= obstacle.left + EPSILON
      || left >= obstacle.right - EPSILON,
    );
  }
  if (Math.abs(from.x - to.x) < EPSILON) {
    const top = Math.min(from.y, to.y);
    const bottom = Math.max(from.y, to.y);
    return obstacles.every((obstacle) =>
      from.x <= obstacle.left + EPSILON
      || from.x >= obstacle.right - EPSILON
      || bottom <= obstacle.top + EPSILON
      || top >= obstacle.bottom - EPSILON,
    );
  }
  return false;
}

function pointKey(point: BunkerPoint): string {
  return `${point.x.toFixed(4)}:${point.y.toFixed(4)}`;
}

function shortestOrthogonalPath(
  start: BunkerPoint,
  end: BunkerPoint,
  obstacles: RouteObstacle[],
): BunkerPoint[] | null {
  const xValues = [...new Set([start.x, end.x, ...obstacles.flatMap((item) => [item.left, item.right])])].sort((a, b) => a - b);
  const yValues = [...new Set([start.y, end.y, ...obstacles.flatMap((item) => [item.top, item.bottom])])].sort((a, b) => a - b);
  const points = new Map<string, BunkerPoint>();
  for (const x of xValues) {
    for (const y of yValues) {
      const point = { x, y };
      if (!obstacles.some((obstacle) => pointInsideObstacle(point, obstacle))) points.set(pointKey(point), point);
    }
  }

  const adjacency = new Map<string, Array<{ key: string; axis: PassageAxis; length: number }>>();
  function linkGroup(group: BunkerPoint[], axis: PassageAxis) {
    const ordered = [...group].sort((a, b) => axis === "horizontal" ? a.x - b.x : a.y - b.y);
    for (let index = 1; index < ordered.length; index += 1) {
      const left = ordered[index - 1]!;
      const right = ordered[index]!;
      if (!segmentAvoidsObstacles(left, right, obstacles)) continue;
      const leftKey = pointKey(left);
      const rightKey = pointKey(right);
      const length = Math.abs(right.x - left.x) + Math.abs(right.y - left.y);
      adjacency.set(leftKey, [...(adjacency.get(leftKey) ?? []), { key: rightKey, axis, length }]);
      adjacency.set(rightKey, [...(adjacency.get(rightKey) ?? []), { key: leftKey, axis, length }]);
    }
  }
  for (const y of yValues) linkGroup([...points.values()].filter((point) => Math.abs(point.y - y) < EPSILON), "horizontal");
  for (const x of xValues) linkGroup([...points.values()].filter((point) => Math.abs(point.x - x) < EPSILON), "vertical");

  type SearchAxis = PassageAxis | "start";
  interface SearchEntry { node: string; axis: SearchAxis; cost: number; state: string }
  const startKey = pointKey(start);
  const endKey = pointKey(end);
  if (!points.has(startKey) || !points.has(endKey)) return null;
  const queue: SearchEntry[] = [{ node: startKey, axis: "start", cost: 0, state: `${startKey}|start` }];
  const distances = new Map([[`${startKey}|start`, 0]]);
  const previous = new Map<string, string>();
  let finalState: string | null = null;

  while (queue.length) {
    queue.sort((a, b) => a.cost - b.cost || a.state.localeCompare(b.state));
    const current = queue.shift()!;
    if (current.cost !== distances.get(current.state)) continue;
    if (current.node === endKey) {
      finalState = current.state;
      break;
    }
    for (const edge of adjacency.get(current.node) ?? []) {
      const bendCost = current.axis !== "start" && current.axis !== edge.axis ? 2 : 0;
      const cost = current.cost + edge.length + bendCost;
      const stateKey = `${edge.key}|${edge.axis}`;
      if (cost >= (distances.get(stateKey) ?? Number.POSITIVE_INFINITY) - EPSILON) continue;
      distances.set(stateKey, cost);
      previous.set(stateKey, current.state);
      queue.push({ node: edge.key, axis: edge.axis, cost, state: stateKey });
    }
  }
  if (!finalState) return null;

  const reversed: BunkerPoint[] = [];
  let cursor: string | undefined = finalState;
  while (cursor) {
    reversed.push(points.get(cursor.slice(0, cursor.lastIndexOf("|")))!);
    cursor = previous.get(cursor);
  }
  return reversed.reverse();
}

function compressOrthogonalPoints(points: BunkerPoint[]): BunkerPoint[] {
  const compressed: BunkerPoint[] = [];
  for (const point of points) {
    const previous = compressed.at(-1);
    if (previous && Math.abs(previous.x - point.x) < EPSILON && Math.abs(previous.y - point.y) < EPSILON) continue;
    const before = compressed.at(-2);
    if (before && previous) {
      const sameX = Math.abs(before.x - previous.x) < EPSILON && Math.abs(previous.x - point.x) < EPSILON;
      const sameY = Math.abs(before.y - previous.y) < EPSILON && Math.abs(previous.y - point.y) < EPSILON;
      if (sameX || sameY) {
        compressed[compressed.length - 1] = point;
        continue;
      }
    }
    compressed.push(point);
  }
  return compressed;
}

function obstacleSafeRoute(
  connection: SiteConnection,
  fromSpace: Space,
  toSpace: Space,
  state: PassageState,
  visibleSpaces: Space[],
): BunkerRoute | null {
  const obstacles = routeObstacles(visibleSpaces);
  const fromObstacle = obstacles.find((item) => item.id === fromSpace.id)!;
  const toObstacle = obstacles.find((item) => item.id === toSpace.id)!;
  const fromRect = spaceRect(fromSpace);
  const toRect = spaceRect(toSpace);
  let best: { from: RoutePortal; to: RoutePortal; points: BunkerPoint[]; score: number } | null = null;

  for (const fromSide of PORT_ORDER) {
    const from = routePortal(fromRect, fromObstacle, fromSide);
    const withoutFrom = obstacles.filter((item) => item.id !== fromSpace.id);
    if (!segmentAvoidsObstacles(from.point, from.stub, withoutFrom)) continue;
    for (const toSide of PORT_ORDER) {
      const to = routePortal(toRect, toObstacle, toSide);
      const withoutTo = obstacles.filter((item) => item.id !== toSpace.id);
      if (!segmentAvoidsObstacles(to.stub, to.point, withoutTo)) continue;
      const middle = shortestOrthogonalPath(from.stub, to.stub, obstacles);
      if (!middle) continue;
      const points = compressOrthogonalPoints([from.point, ...middle, to.point]);
      const distance = points.slice(1).reduce((total, point, index) => {
        const previous = points[index]!;
        return total + Math.abs(point.x - previous.x) + Math.abs(point.y - previous.y);
      }, 0);
      const score = distance + Math.max(0, points.length - 2) * 2;
      if (!best || score < best.score - EPSILON) best = { from, to, points, score };
    }
  }
  if (!best) return null;

  const segments = best.points.slice(1).map((point, index) => {
    const previous = best!.points[index]!;
    return Math.abs(previous.y - point.y) < EPSILON
      ? horizontalSegment(connection.id, index, previous.x, point.x, previous.y, state)
      : verticalSegment(connection.id, index, previous.x, previous.y, point.y, state);
  });
  const bends = best.points.slice(1, -1);
  return {
    ...connection,
    state,
    kind: segments.length === 1 ? segments[0]!.axis : "dogleg",
    fromPort: best.from.side,
    toPort: best.to.side,
    fromPoint: best.from.point,
    toPoint: best.to.point,
    bend: bends[0],
    bends,
    sharedWall: false,
    segments,
  };
}

export function buildBunkerRoutes(
  spaces: Space[],
  connections: SiteConnection[],
): BunkerRoute[] {
  const visibleSpaces = spaces.filter((space) => space.discovered);
  const byId = new Map(visibleSpaces.map((space) => [space.id, space]));

  return [...connections]
    .sort((left, right) => left.id.localeCompare(right.id))
    .flatMap((connection): BunkerRoute[] => {
      const from = byId.get(connection.from);
      const to = byId.get(connection.to);
      if (!from || !to) return [];
      const state = passageState(from, to);
      const route = sharedWallRoute(connection, from, to, state)
        ?? obstacleSafeRoute(connection, from, to, state, spaces);
      return route ? [route] : [];
    });
}

function portOffset(rect: SpaceRect, side: RoomEdge, point: BunkerPoint): number {
  const raw = side === "west" || side === "east"
    ? ((point.y - rect.top) / rect.height) * 100
    : ((point.x - rect.left) / rect.width) * 100;
  return Math.max(8, Math.min(92, raw));
}

export function connectionPortsBySpace(
  routes: BunkerRoute[],
  spaces: Space[] = [],
): Map<string, ConnectionPort[]> {
  const rects = new Map(spaces.map((space) => [space.id, spaceRect(space)]));
  const ports = new Map<string, ConnectionPort[]>();
  for (const route of routes) {
    const fromRect = rects.get(route.from);
    const toRect = rects.get(route.to);
    ports.set(route.from, [
      ...(ports.get(route.from) ?? []),
      {
        id: route.id,
        side: route.fromPort,
        offset: fromRect ? portOffset(fromRect, route.fromPort, route.fromPoint) : 50,
        sharedWall: route.sharedWall,
      },
    ]);
    ports.set(route.to, [
      ...(ports.get(route.to) ?? []),
      {
        id: route.id,
        side: route.toPort,
        offset: toRect ? portOffset(toRect, route.toPort, route.toPoint) : 50,
        sharedWall: route.sharedWall,
      },
    ]);
  }
  return ports;
}
